import React, { useState } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import PropTypes from 'prop-types';
import { Ionicons } from '@expo/vector-icons';

import * as Layout from '@components/screen/Layout';
import SociollaCard from '@components/sociolla/SociollaCard';
import SociollaText from '@components/sociolla/SociollaText';
import Highcharts from '@components/chart/Highcharts';
import Colors from '@global/colors';
import { isWeb } from '@global/screens';

// Fixed per code so GT is always blue and MT always teal whatever order the API returns.
const MARKET_TYPE_COLORS = { GT: Colors.blue0, MT: Colors.green2 };
const FALLBACK_COLORS = [Colors.yellow0, Colors.red0, Colors.grey13];

const normalise = (code) =>
  String(code || '')
    .trim()
    .toUpperCase();

// The API returns codes only. Names come from the market_types resource, matched on the
// same trimmed, uppercased code the backend grouped by. A code with no match shows as-is.
const labelFor = (marketTypes, marketCode, businessCode) => {
  const market = marketTypes.find((m) => normalise(m.code) === marketCode);
  if (!businessCode) {
    return market?.name || marketCode;
  }
  const business = (market?.business_type || []).find(
    (b) => normalise(b.code) === businessCode,
  );
  return business?.name || businessCode;
};

const SalesmanBusinessTypeChart = ({
  rows,
  marketTypes,
  loading,
  error,
  style,
}) => {
  const [expanded, setExpanded] = useState(false);

  const total = rows.reduce((sum, row) => sum + (row.count || 0), 0);
  // Bars are scaled against the largest business type across every market type, so a bar
  // in the MT list can be read against one in the GT list.
  const maxCount = rows.reduce(
    (max, row) =>
      row.business_types.reduce((m, b) => Math.max(m, b.count || 0), max),
    0,
  );

  const barWidth = (count) => `${maxCount ? (count / maxCount) * 100 : 0}%`;

  let fallbackIndex = 0;
  const colored = rows.map((row) => {
    let color = MARKET_TYPE_COLORS[row.market_type_code];
    if (!color) {
      color = FALLBACK_COLORS[fallbackIndex % FALLBACK_COLORS.length];
      fallbackIndex += 1;
    }
    return { ...row, color };
  });

  const body = () => {
    if (loading) {
      return <Layout.Loader />;
    }
    if (error) {
      return (
        <SociollaText style={styles.unavailable}>Unavailable</SociollaText>
      );
    }
    return (
      <>
        <View style={styles.donutRow}>
          <View style={styles.donut}>
            <Highcharts
              type="pie"
              height={220}
              innerSize="55%"
              showDataLabels={false}
              centerText={String(total)}
              colors={colored.map((row) => row.color)}
              pieTooltipFormat="{point.name}: <b>{point.y}</b>"
              data={[
                {
                  name: '',
                  data: colored.map((row) => ({
                    name: row.market_type_code,
                    y: row.count,
                  })),
                },
              ]}
            />
          </View>

          <View style={styles.legend}>
            {colored.map((row) => (
              <View key={row.market_type_code} style={styles.legendItem}>
                <View style={[styles.swatch, { backgroundColor: row.color }]} />
                <SociollaText style={styles.legendCode}>
                  {`${row.market_type_code}:`}
                </SociollaText>
                <SociollaText type="medium" style={styles.legendCount}>
                  {row.count}
                </SociollaText>
              </View>
            ))}
          </View>
        </View>

        <TouchableOpacity
          activeOpacity={0.8}
          style={styles.toggle}
          onPress={() => setExpanded((prev) => !prev)}>
          <SociollaText style={styles.toggleText}>View details</SociollaText>
          <Ionicons
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={16}
            color={Colors.blue0}
          />
        </TouchableOpacity>

        {expanded ? (
          <View style={styles.details}>
            {colored.map((row) => (
              <View key={row.market_type_code} style={styles.group}>
                <View style={styles.groupHeader}>
                  <View
                    style={[styles.swatch, { backgroundColor: row.color }]}
                  />
                  <SociollaText type="medium" style={styles.groupCode}>
                    {row.market_type_code}
                  </SociollaText>
                </View>

                {/* No cap: a cap would drop the tail categories this chart exists to show. */}
                {row.business_types.map((business) => (
                  <View key={business.code} style={styles.bar}>
                    <SociollaText style={styles.barLabel} numberOfLines={1}>
                      {labelFor(
                        marketTypes,
                        row.market_type_code,
                        business.code,
                      )}
                    </SociollaText>
                    <View style={styles.track}>
                      <View
                        style={[
                          styles.fill,
                          {
                            backgroundColor: row.color,
                            width: barWidth(business.count),
                          },
                        ]}
                      />
                    </View>
                    <SociollaText style={styles.barCount}>
                      {business.count}
                    </SociollaText>
                  </View>
                ))}
              </View>
            ))}
          </View>
        ) : null}
      </>
    );
  };

  return (
    <SociollaCard style={[styles.card, style]}>
      <SociollaText type="medium" style={styles.title}>
        Visit by Business Type
      </SociollaText>
      <View style={styles.body}>{body()}</View>
    </SociollaCard>
  );
};

SalesmanBusinessTypeChart.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      market_type_code: PropTypes.string,
      count: PropTypes.number,
      business_types: PropTypes.arrayOf(
        PropTypes.shape({ code: PropTypes.string, count: PropTypes.number }),
      ),
    }),
  ),
  marketTypes: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

SalesmanBusinessTypeChart.defaultProps = {
  rows: [],
  marketTypes: [],
  loading: false,
  error: false,
  style: null,
};

const styles = StyleSheet.create({
  bar: { alignItems: 'center', flexDirection: 'row', marginBottom: 8 },
  barCount: {
    color: Colors.textSecondary,
    fontSize: 12,
    minWidth: 28,
    textAlign: 'right',
  },
  barLabel: { color: Colors.grey13, fontSize: 12, width: isWeb ? 160 : 120 },
  body: { justifyContent: 'center', minHeight: 220 },
  card: { marginBottom: 0, padding: 16 },
  details: { marginTop: 12 },
  // Explicit height too: on iOS the wrapping row measures the WebView without its basis.
  donut: { flexBasis: 220, flexGrow: 0, height: 220, width: 220 },
  donutRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    justifyContent: 'center',
  },
  fill: { borderRadius: 3, height: '100%' },
  group: { marginBottom: 12 },
  groupCode: { fontSize: 13 },
  groupHeader: { alignItems: 'center', flexDirection: 'row', marginBottom: 8 },
  legend: { flexBasis: 140, flexGrow: 1 },
  legendCode: { color: Colors.grey13, fontSize: 13, marginRight: 6 },
  legendCount: { fontSize: 13 },
  legendItem: { alignItems: 'center', flexDirection: 'row', marginBottom: 8 },
  swatch: { borderRadius: 2, height: 10, marginRight: 8, width: 10 },
  title: { fontSize: 16, marginBottom: 16 },
  toggle: {
    alignItems: 'center',
    borderTopColor: Colors.grey0,
    borderTopWidth: 1,
    flexDirection: 'row',
    marginTop: 16,
    paddingTop: 12,
  },
  toggleText: { color: Colors.blue0, fontSize: 13, marginRight: 4 },
  track: {
    backgroundColor: Colors.grey5,
    borderRadius: 3,
    flex: 1,
    height: 6,
    marginHorizontal: 10,
  },
  unavailable: { color: Colors.red0, fontSize: 16, textAlign: 'center' },
});

export default SalesmanBusinessTypeChart;
